import { SoundbitesSortTypeStrings } from 'components/insightContent/enum/SoundbitesSortType'
import i18n from 'i18n'
import ComparisonMode, { ComparisonModeStrings } from 'enums/ComparisonMode'
import { CountTypeStrings } from 'enums/CountType'
import { InsightViewOptionStrings } from 'enums/InsightViewOption'
import WidgetType, { WidgetTypeStrings } from 'enums/WidgetType'
import { SentimentStrings } from 'enums/Sentiment'
import { GenderStrings } from 'enums/Gender'
import MeasureType, { MeasureTypeStrings } from 'enums/MeasureType'

const messageUtil = Object.freeze({
  measureType: (measureType: string) => {
    const measureTypeMap: Record<string, string> = {
      TotalEngagements: 'totalEngagement_other',
      TWRetweets: 'retweet_other',
      TWReplies: 'reply_other',
      SWReblogs: 'reblog_other',
      Reposts: 'repost_other',
      SWRetweets: 'repost_other',
      TotalReposts: 'repost_other',
      Replies: 'replyAndComment_other',
      SWReplies: 'replyAndComment_other',
      TotalReplies: 'replyAndComment_other',
      Mentions: 'mention_other',
      TotalBuzz: 'mention_other',
      PostImpressions: 'potentialImpression',
      Impressions: 'potentialImpression',
      PositiveComment: 'positive_other',
      PositiveReply: 'positive_other',
      Positives: 'positive_other',
      PositiveSentiment: 'positive_other',
      PositiveSentimentOnUniqueAuthors: 'positive_other',
      NegativeComment: 'negative_other',
      NegativeReply: 'negative_other',
      Negatives: 'negative_other',
      NegativeSentiment: 'negative_other',
      NegativeSentimentOnUniqueAuthors: 'negative_other',
      NetSentiment: 'netSentiment',
      NetSentimentOnUniqueAuthors: 'netSentiment',
      PassionIntensity: 'passionIntensity',
      Passion: 'passionIntensity',
      PassionOnUniqueAuthors: 'passionIntensity',
      Likes: 'like_other',
      Posts: 'post_other',
      TotalBuzzPost: 'post_other',
      UniqueAuthors: 'author_other',
      Shares: 'share_other',
      OriginalPosts: 'originalPost_other',
      Owned: 'owned',
      Partnered: 'partnered',
      Earned: 'earned',
      Twitter: 'twitter',
      Facebook: 'facebook',
      Instagram: 'instagram',
      YouTube: 'youTube',
      TikTok: 'tikTok',
      Other: 'other',
      Neutrals: 'neutral_other',
      Male: 'male',
      Female: 'female',
      Unknown: 'unknown',
      PostVideos: 'video_other',
      PostImages: 'image_other',
      PostOther: 'other',
      Engagements: 'totalEngagement_other',
      EngagementRatioOnly: 'engagementRatio',
      Views: 'viewsListens',
      Dislikes: 'dislike_other',
      Comments: 'comment_other',
      EngagementRatio: 'engagementRatio',
      OverallStarRatings: 'overallStarRating_other',
      OverallStarRatingsAverage: 'overallStarRating_other',
      OverallSurveyScores: 'overallSurveyScore_other',
      OverallSurveyScoresAverage: 'overallSurveyScore_other',
      SurveyScores: 'overAll',
      StarRatings: 'overAll',
      NetPromoterScores: 'netPromoterScore_other',
      FiveStar: 'fiveStar',
      FourStar: 'fourStar',
      ThreeStar: 'threeStar',
      TwoStar: 'twoStar',
      OneStar: 'oneStar',
      TenScore: 'tenScore',
      HighScore: 'highScore',
      MediumScore: 'mediumScore',
      LowScore: 'lowScore',
      NoScore: 'noScore',
      Promoter: 'promoter_other',
      Passive: 'passive_other',
      Detractor: 'detractor_other',
      NoResponseStar: 'noResponse',
      NoResponseSurvey: 'noResponse',
      NoResponseNPS: 'noResponse',
      Sum: 'total',
      Score: 'score'
    }
    return i18n.t(measureTypeMap[measureType])
  },
  measurementType: (type: string) => {
    const measureMentTypeMap: Record<string, string> = {
      StarOV: 'overallStarRating',
      SurveyOV: 'overallSurveyScore',
      NPS: 'netPromoterScore'
    }
    return i18n.t(measureMentTypeMap[type])
  },
  comparisonModeOption: (mode: string) => {
    const comparisonModeOptionMap: Record<string, string> = {
      TOPICS: 'topic_other',
      THEMES: 'theme_other',
      TAGS: 'tag_other',
      DOC_HIERARCHIES: 'product_other'
    }
    return i18n.t(comparisonModeOptionMap[mode])
  },
  comparisonModeOptionSingular: (mode: string) => {
    const comparisonModeOptionSingularMap: Record<string, string> = {
      TOPICS: 'topic',
      THEMES: 'theme',
      TAGS: 'tag',
      DOC_HIERARCHIES: 'product'
    }
    return i18n.t(comparisonModeOptionSingularMap[mode])
  },
  timelineYAxisGroupTitle: (group: string) => {
    const timelineYAxisGroupTitleMap: Record<string, string> = {
      SW_SENTIMENT: 'positiveAndNegative_other',
      SW_CALCULATED_SENTIMENT: 'sentimentScore_other',
      SW_IMPRESSION: 'potentialImpression_other',
      SW_BUZZ: 'postAndMention_other',
      SW_BUZZ_POST: 'post_other',
      SW_ENGAGEMENTS: 'engagement_other',
      SW_ENGAGEMENT_RATIO: 'engagementRatio',
      SW_CALCULATED_SCORES: 'ratingAndScore_other',
      SW_NET_PROMOTER_SCORES: 'netPromoterScore_other',
      STATISTICS: 'statistic_other'
    }
    return i18n.t(timelineYAxisGroupTitleMap[group])
  },
  sentiments: (sentiment: SentimentStrings) => {
    const sentimentMap: Record<string, string> = {
      Positive: 'positive_other',
      Negative: 'negative_other',
      Neutral: 'neutral_other',
      Mixed: 'mixed',
      None: 'none'
    }
    return i18n.t(sentimentMap[sentiment])
  },
  genders: (gender: GenderStrings) => {
    const genderMap: Record<string, string> = {
      Male: 'male',
      Female: 'female',
      Unknown: 'unknown'
    }
    return i18n.t(genderMap[gender])
  },
  sources: (source: string) => {
    const sourceMap: Record<string, string> = {
      Other: 'other',
      Patents: 'patent_other',
      Journals: 'journal_other',
      Shopping: 'shopping',
      Blogs: 'blog_other',
      Forums: 'forum_other',
      News: 'news',
      Broadcast: 'broadcast',
      Podcast: 'podcast',
      Education: 'education',
      Government: 'government',
      Organization: 'organization',
      Company: 'company',
      SocialNetworks: 'otherSocialNetwork_other',
      Microblogs: 'microblog_other',
      ProfReviews: 'professionalReview_other',
      Comments: 'comment_other',
      ConsumerReviews: 'consumerReview_other',
      Twitter: 'twitter',
      Facebook: 'facebook',
      Instagram: 'instagram',
      YouTube: 'youTube',
      TikTok: 'tikTok',
      Internal: 'internal',
      Bluesky: 'bluesky',
      Reddit: 'reddit',

      SourceOther: 'other',
      SourcePatents: 'patent_other',
      SourceJournals: 'journal_other',
      SourceShopping: 'shopping',
      SourceBlogs: 'blog_other',
      SourceForums: 'forum_other',
      SourceNews: 'news',
      SourceBroadcast: 'broadcast',
      SourceEducation: 'education',
      SourceGovernment: 'government',
      SourceOrganization: 'organization',
      SourceCompany: 'company',
      SourceSocialNetworks: 'otherSocialNetwork_other',
      SourceMicroblogs: 'microblog_other',
      SourceProfReviews: 'professionalReview_other',
      SourceComments: 'comment_other',
      SourceConsumerReviews: 'consumerReview_other',
      SourceTwitter: 'twitter',
      SourceFacebook: 'facebook',
      SourceInstagram: 'instagram',
      SourceYouTube: 'youTube',
      SourceTikTok: 'tikTok',
      SourceBluesky: 'bluesky',
      SourceReddit: 'reddit',
      SourceInternal: 'internal',

      SourceOtherPosts: 'other',
      SourcePatentsPosts: 'patent_other',
      SourceJournalsPosts: 'journal_other',
      SourceShoppingPosts: 'shopping',
      SourceBlogsPosts: 'blog_other',
      SourceForumsPosts: 'forum_other',
      SourceNewsPosts: 'news',
      SourceBroadcastPosts: 'broadcast',
      SourceEducationPosts: 'education',
      SourceGovernmentPosts: 'government',
      SourceOrganizationPosts: 'organization',
      SourceCompanyPosts: 'company',
      SourceSocialNetworksPosts: 'otherSocialNetwork_other',
      SourceMicroblogsPosts: 'microblog_other',
      SourceProfReviewsPosts: 'professionalReview_other',
      SourceCommentsPosts: 'comment_other',
      SourceConsumerReviewsPosts: 'consumerReview_other',
      SourceTwitterPosts: 'twitter',
      SourceFacebookPosts: 'facebook',
      SourceInstagramPosts: 'instagram',
      SourceYouTubePosts: 'youTube',
      SourceTikTokPosts: 'tikTok',
      SourceBlueskyPosts: 'bluesky',
      SourceRedditPosts: 'reddit',
      SourceInternalPosts: 'internal'
    }
    return i18n.t(sourceMap[source])
  },
  emojisLabel: (emojiName: string) => {
    const emojisLabelMap: Record<string, string> = {
      ALL: 'all',
      ALL_TYPES: 'allTypes',
      ALL_EMOTIONS: 'allEmotions',
      ANGER: 'anger',
      BOREDOM: 'boredom',
      CONFIDENCE: 'confidence',
      EMBARRASSMENT: 'embarrassment',
      EXCITEMENT: 'excitement',
      FEAR: 'fear',
      HAPPINESS: 'happiness',
      LOVE: 'love',
      SADNESS: 'sadness',
      SURPRISE: 'surprise',
      ANIMALS: 'animal_other',
      BODY_PARTS: 'bodyPart_other',
      CELEBRATION_AND_HOLIDAYS: 'celebrationAndHoliday_other',
      CLOTHING: 'clothing',
      DRINKS: 'drinks',
      ELECTRONICS: 'electronic_other',
      FACES: 'face_other',
      FLAGS: 'flag_other',
      FLOWERS_AND_TREES: 'flowerAndTree_other',
      FOOD: 'food',
      FRUITS_AND_VEGGIES: 'fruitAndVeggie_other',
      HAND_GESTURES: 'handGesture_other',
      MONEY: 'money',
      MUSIC: 'music',
      OTHERS: 'other_other',
      PERSON: 'person',
      PLACES_AND_BUILDINGS: 'placeAndBuilding_other',
      PRAYER_AND_RELIGIOUS: 'prayerAndReligious',
      ROMANCE: 'romance',
      SIGNS_AND_SYMBOLS: 'signAndSymbol_other',
      SPORTS_AND_HOBBIES: 'sportAndHobby_other',
      TIME_AND_DATE: 'timeAndDate',
      TRAVEL: 'travel',
      WEAPONS: 'weapon_other',
      WEATHER: 'weather',
      ZODIAC: 'zodiac'
    }
    return i18n.t(emojisLabelMap[emojiName])
  },
  photoConceptObjectLabel: (name: string) => {
    const photoConceptObjectLabelMap: Record<string, string> = {
      person: 'person',
      bicycle: 'bicycle',
      car: 'car',
      bus: 'bus',
      train: 'train',
      cat: 'cat',
      dog: 'dog',
      tie: 'tie',
      bottle: 'bottle',
      wine_glass: 'wineGlass',
      cup: 'cup',
      fork: 'fork',
      spoon: 'spoon',
      sandwich_burger: 'sandwichBurger',
      pizza: 'pizza',
      chair: 'chair',
      couch: 'couch',
      plant: 'plant',
      table: 'table',
      sink: 'sink',
      motorcycle: 'motorcycle',
      keyboard: 'keyboard',
      handbag: 'handbag',
      bench: 'bench',
      truck: 'truck',
      airplane: 'airplane',
      backpack: 'backpack',
      traffic_light: 'Traffic Light',
      bowl: 'bowl',
      boat: 'boat'
    }
    return i18n.t(photoConceptObjectLabelMap[name])
  },
  photoConceptScenesLabel: (name: string) => {
    const photoConceptScenesLabelMap: Record<string, string> = {
      beach: 'beach',
      cliff: 'cliff',
      coast: 'coast',
      creek: 'creek',
      downtown: 'downtown',
      house: 'house',
      japanese_garden: 'japaneseGarden',
      kitchen: 'kitchen',
      lagoon: 'lagoon',
      lake: 'lake',
      lawn: 'lawn',
      mountain: 'mountain',
      ocean: 'ocean',
      palace: 'palace',
      plaza: 'plaza',
      river: 'river',
      soccer_field: 'SoccerField',
      stadium: 'stadium',
      street: 'street',
      swimming_pool: 'swimmingPool'
    }
    return i18n.t(photoConceptScenesLabelMap[name])
  },
  photoConceptEmotionsLabel: (name: string) => {
    const photoConceptEmotionsLabelMap: Record<string, string> = {
      happy: 'happy',
      neutral: 'neutral',
      sad: 'sad'
    }
    return i18n.t(photoConceptEmotionsLabelMap[name])
  },
  professionsLabel: (type: string) => {
    const professionsLabelMap: Record<string, string> = {
      ED: 'education',
      CA: 'creativeArt_other',
      BL: 'blogging',
      EN: 'entrepreneurship',
      EM: 'executiveManagement',
      BF: 'bankingAndFinance',
      HM: 'healthAndMedicine',
      LO: 'lowAndOrder',
      PG: 'politicsAndGoverment',
      JO: 'journalism',
      RE: 'religion',
      HO: 'hospitality',
      SM: 'salesAndMarketing',
      SR: 'scienceAndResearch',
      SS: 'socialService_other',
      SP: 'sport_other',
      ST: 'student',
      TE: 'technology',
      FF: 'farmingAndFishing',
      OA: 'officeAdministration',
      MA: 'manufacturing',
      UN: 'unemployed',
      CO: 'construction',
      PH: 'personalAndHomeServices',
      HE: 'homemaker',
      TR: 'transportation'
    }
    return i18n.t(professionsLabelMap[type])
  },
  interestsLabel: (type: string) => {
    const interestsLabelMap: Record<string, string> = {
      AU: 'automotive',
      BE: 'beauty',
      LI: 'literature',
      AN: 'aniemeAndComics',
      FA: 'family',
      FS: 'fashion',
      AC: 'artAndCraft_other',
      FD: 'foodAndDrink',
      GA: 'gaming',
      GL: 'greenLiving',
      HF: 'healthAndFitness',
      HG: 'homeAndGarden',
      MO: 'movie_other',
      MU: 'music',
      OU: 'outdoor_other',
      PE: 'pet_other',
      PV: 'photoAndVideo',
      PO: 'politic_other',
      RE: 'religion',
      SH: 'shopping',
      SP: 'sport_other',
      TV: 'television',
      TE: 'technology',
      TR: 'travel'
    }
    return i18n.t(interestsLabelMap[type])
  },
  ethnicityLabel: (type: string) => {
    const ethnicityLabelMap: Record<string, string> = {
      AmericanIndianOrAlaskanNative: 'americanIndianOrAlaskanNative',
      AsianOrPacificIslander: 'asian',
      Hispanic: 'hispanic',
      TwoRaces: 'other',
      White: 'caucasian',
      Black: 'aficanAmerican'
    }
    return i18n.t(ethnicityLabelMap[type])
  },
  countType: (countType: CountTypeStrings) => {
    const countTypeMap: Record<string, string> = {
      POSTS: 'post_other',
      MENTIONS: 'mention_other',
      AUTHORS: 'author_other'
    }
    return i18n.t(countTypeMap[countType])
  },
  tooltipSeriesHeader: (widgetType: WidgetTypeStrings, option?: ComparisonModeStrings) => {
    switch (widgetType) {
      case WidgetType.SOCIAL_WEB_SOURCES:
        return i18n.t('source')
      case WidgetType.SOCIAL_WEB_TIMELINE_COMPARISON:
      case WidgetType.AUDIENCE_TIMELINE_COMPARISON:
        if (option) {
          const optionLangKey = {
            [ComparisonMode.TOPICS]: 'topic',
            [ComparisonMode.THEMES]: 'theme',
            [ComparisonMode.TAGS]: 'tag',
            [ComparisonMode.DOC_HIERARCHIES]: 'product'
          }[option]
          return i18n.t(optionLangKey)
        }
        return ''

      default:
        return ''
    }
  },
  sentimentSingular: (type: string) => {
    const sentimentSingularMap: Record<string, string> = {
      Mixed: 'mixed',
      Positive: 'positive',
      Negative: 'negative',
      Neutral: 'neutral',
      None: 'none'
    }
    return i18n.t(sentimentSingularMap[type])
  },
  highlightRoleInsight: (type: string) => {
    const highlighRoleInsightMap: Record<string, string> = {
      Attribute: 'attribute',
      Emotion: 'emotion',
      Behavior: 'behavior'
    }
    return i18n.t(highlighRoleInsightMap[type])
  },
  gendersIconMessage: (gender: GenderStrings) => {
    const genderIconMap: Record<string, string> = {
      Male: 'male',
      Female: 'female',
      Unknown: 'unknownGender'
    }
    return i18n.t(genderIconMap[gender])
  },
  viewOption: (viewOption: InsightViewOptionStrings) => {
    const viewOptionMap: Record<string, string> = {
      UNIQUE_AUTHORS: 'author_other',
      POSTS: 'post_other',
      NET_SENTIMENT: 'netSentiment',
      IMPRESSIONS: 'potentialImpression_other',
      PASSION_INTENSITY: 'passionIntensity',
      POSITIVE: 'positive_other',
      NEGATIVE: 'negative_other',
      AUTHORS: 'author_other',
      BRANDS: 'brand_other',
      HASHTAGS: 'hashtag_other',
      MENTIONS: 'mention_other',
      POSITIVE_NEGATIVE: 'positiveAndNegative_other',
      POSITIVE_NEGATIVE_NEUTRAL: 'positiveNegativeNeutural_other',
      TERMS: 'term_other',
      THINGS: 'thing_other',
      MOST_MENTIONS: 'byCount',
      MOST_FOLLOWERS: 'byFollowersVisitors',
      PEOPLE: 'people',
      ATTRIBUTES: 'attribute_other',
      EMOTIONS: 'emotion_other',
      BEHAVIORS: 'behavior_other',
      SOURCES: 'source_other',
      MALE_FEMALE: 'gender',
      ALL_SOURCES: 'all',
      BLOGS: 'blog_other',
      FORUMS: 'forum_other',
      SUBREDDITS: 'subReddit_other',
      NEWS: 'news',
      EMOTIONS_BEHAVIORS_TO_THINGS: 'emotionAndBehavior_other',
      THINGS_TO_EMOTIONS_BEHAVIORS: 'thing_other',
      BIOTEXT: 'bioTerm_other',
      ENGAGEMENTS: 'totalEngagement_other',
      ENGAGEMENT_RATIO_ONLY: 'engagementRatio',
      MOST_ENGAGEMENT: 'byTotalEngagement_other',
      SHARES: 'share_other',
      LIKES: 'like_other',
      COMMENTS: 'comment_other',
      VIEWS: 'view_other',
      DISLIKES: 'dislike_other',
      SCORE: 'score_other',
      LOGOS: 'logo_other',
      DETECT_OBJECTS: 'object_other',
      DETECT_SCENES: 'scene_other',
      DETECT_EMOTIONS: 'emotion_other',
      AGE: 'age',
      INTERESTS: 'interest_other',
      PROFESSIONS: 'profession_other',
      ETHNICITY: 'ethnicity',
      EMOJIS_GROUP: 'type_other',
      EMOJIS_EMOTION: 'emotion_other',
      EMOJIS_EMOJI: 'individualEmoji_other',
      PER_CAPITA: 'perCapita',
      PERCENT_SHARE: 'percentShare',
      HOUR_OF_DAY: 'hourOfDay',
      DAY_OF_WEEK: 'dayOfWeek',
      HOUR_OF_WEEK: 'dayAndHour',
      LANGUAGE: 'language',
      TAGS: 'tag_other',
      DOC_HIERARCHY: 'productHierarchy_other',
      PRODUCT_NAME: 'productName_other',
      OVERVIEW: 'overview',
      OWNED_PARTNERED_EARNED: 'convergedMedia',
      CHANNELS: 'domain_other',
      STAR_RATINGS: 'starRating_other',
      SURVEY_SCORES: 'surveyScore_other',
      BY_ENGAGEMENTS: 'byTotalEngagement_other',
      BY_REPOSTS: 'byRepost_other',
      BY_REPLIES: 'byReplyAndComment_other',
      BY_DATE: 'byDate',
      BY_LINK_MENTIONS: 'byLinkMention_other'
    }
    return i18n.t(viewOptionMap[viewOption])
  },
  getTooltipLabelByMeasureType: (measure: MeasureTypeStrings) => {
    const measureMap: Record<string, string> = {
      [MeasureType.MENTIONS]: 'tooltipContentByMeasureType.mentions',
      [MeasureType.POSTS]: 'tooltipContentByMeasureType.posts',
      [MeasureType.IMPRESSIONS]: 'tooltipContentByMeasureType.impressions',
      [MeasureType.NET_SENTIMENT]: 'tooltipContentByMeasureType.netSentiment',
      [MeasureType.PASSION_INTENSITY]: 'tooltipContentByMeasureType.passionIntensity',
      [MeasureType.POSITIVES]: 'tooltipContentByMeasureType.positives',
      [MeasureType.NEGATIVES]: 'tooltipContentByMeasureType.negatives',
      [MeasureType.UNIQUE_AUTHORS]: 'tooltipContentByMeasureType.uniqueAuthors',
      [MeasureType.ENGAGEMENTS]: 'tooltipContentByMeasureType.engagements',
      [MeasureType.ENGAGEMENT_RATIO]: 'tooltipContentByMeasureType.engagementRatio',
      [MeasureType.ENGAGEMENT_RATIO_ONLY]: 'tooltipContentByMeasureType.engagementRatio',
      [MeasureType.COMMENTS]: 'tooltipContentByMeasureType.comments',
      [MeasureType.LIKES]: 'tooltipContentByMeasureType.likes',
      [MeasureType.SHARES]: 'tooltipContentByMeasureType.shares',
      [MeasureType.SCORE]: 'tooltipContentByMeasureType.score'
    }
    return i18n.t(measureMap[measure])
  },
  trendingLastPeriodString: (timeUnit: string | undefined | null) => {
    const trendingLastPeriodStringMap: Record<string, string> = {
      Quarter: 'lastQuarter',
      Month: 'lastMonth',
      Week: 'lastWeek',
      Day: 'lastDay',
      Hour: 'lastHour',
      QuarterHour: 'lastQuarterHour',
      Minute: 'lastMinute'
    }
    return timeUnit && i18n.t(trendingLastPeriodStringMap[timeUnit])
  },
  trendingHistoryString: (timeUnit: string | null | undefined) => {
    const trendingHistoryStringMap: Record<string, string> = {
      Quarter: 'quarterly',
      Month: 'monthly',
      Week: 'weekly',
      Day: 'daily',
      Hour: 'hourly',
      Minute: 'everyMinute'
    }
    return timeUnit && i18n.t(trendingHistoryStringMap[timeUnit])
  },
  soundbiteSortOptions: (optionVal: SoundbitesSortTypeStrings) => {
    const soundbiteSortOptionsMap: Record<string, string> = {
      RICHNESS: 'richness',
      TIME: 'time',
      FOLLOWERS_VISITORS: 'followersVisitors'
    }
    return optionVal && i18n.t(soundbiteSortOptionsMap[optionVal])
  }
})
export default messageUtil
